import React from 'react';
import { formatTime, linkifyText } from '../utils/utils';
import { LuExternalLink as FiExternalLink } from "react-icons/lu";
import { LuUsers as HiUserGroup } from "react-icons/lu";
import { LuGlobe as FaGlobe } from "react-icons/lu";
import { useNavigate } from 'react-router';
import { ROUTES } from '../App';

const ForwardedMessage = ({
    message,
    isFromCurrentUser,
    type = 'group', // 'group' or 'community'
    onViewClick,
    selectionMode,
    selected,
    onToggleReaction
}) => {
    const navigate = useNavigate();
    const original = message.originalGroup || message.originalCommunity;

    if (!original) return null;

    const handleViewClick = (e) => {
        e.stopPropagation();
        if (type === 'group') {
            navigate(`${ROUTES.GROUP_CHAT}/${original.id}${original.inviteToken ? `?invite=${original.inviteToken}` : ''}`);
        } else {
            navigate(`${ROUTES.COMMUNITY_CHAT}/${original.id}${original.inviteToken ? `?invite=${original.inviteToken}` : ''}`);
        }
        if (onViewClick) onViewClick();
    };

    const handleContentClick = (e) => {
        // If in selection mode, toggle selection when clicking the message
        if (selectionMode) {
            e.preventDefault();
            e.stopPropagation();
            if (onToggleReaction) {
                onToggleReaction(message.id, 'selected', selected ? message.currentReaction : null);
            }
        }
    };

    const getTypeText = () => {
        return type === 'group' ? 'Group' : 'Community';
    };

    return (
        <div className={`flex items-start gap-2 mt-2 ${isFromCurrentUser ? "justify-end" : "justify-start"} w-full`}>

            <div className={`md:max-w-[100%] max-w-[100%] ${isFromCurrentUser ? "items-end" : "items-start"}`}>
                {/* Forwarded message container - styled like WhatsApp */}
                <div
                    onClick={handleContentClick}
                    className={`
                        relative rounded-lg overflow-hidden transition-all w-fit
                        ${isFromCurrentUser
                            ? 'bg-primary text-white'
                            : 'bg-[#F2F7FB] text-black '
                        }
                        ${selectionMode && selected ? 'border-2 border-[#57a1be]' : ''}
                        ${selectionMode ? 'cursor-pointer' : ''}
                    `}
                >
                    {/* Top indicator - "Forwarded" label */}
                    <div className="flex items-center gap-1 px-3 pt-2">
                        <svg
                            className={`w-3.5 h-3.5 ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path d="M7 8a3 3 0 100-6 3 3 0 000 6zm.5 1.5A4.5 4.5 0 003 14v1h2v-1a2.5 2.5 0 015 0v1h2v-1a4.5 4.5 0 00-4.5-4.5zM14 8a2 2 0 100-4 2 2 0 000 4zm.5 1.5A3.5 3.5 0 0011 13v1h2v-1a1.5 1.5 0 013 0v1h2v-1a3.5 3.5 0 00-3.5-3.5z" />
                        </svg>
                        <span className={`text-xs font-medium ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                            Shared
                        </span>
                    </div>

                    {/* Content */}
                    <div className="px-3 pb-2">
                        {/* Original message text */}
                        <p className={`text-sm whitespace-pre-wrap break-words`}
                            style={{
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                            }}>
                            {linkifyText(message.text, isFromCurrentUser)}
                        </p>

                        {/* Group/Community Card - WhatsApp style */}
                        <div
                            onClick={handleViewClick}
                            className={`
                                mt-2 rounded-lg overflow-hidden cursor-pointer
                                ${isFromCurrentUser
                                    ? 'bg-primary/20'
                                    : 'bg-[#F2F7FB]'
                                }
                            `}
                        >
                            <div className="flex items-center p-2 gap-3">
                                {/* Icon/Image */}
                                <div className="relative">
                                    <img
                                        src={original.photo || (
                                            type === 'group'
                                                ? "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346"
                                                : "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/community-default.png?alt=media&token=default"
                                        )}
                                        alt={original.name}
                                        className="w-10 h-10 rounded-lg object-cover"
                                        onError={(e) => {
                                            e.target.src = "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346";
                                        }}
                                    />
                                    {/* Type indicator badge */}
                                    <div className={`
                                        absolute -bottom-1 -right-1 
                                        w-4 h-4 rounded-full 
                                        flex items-center justify-center
                                        ${type === 'group' ? 'bg-primary' : 'bg-green-500'}
                                        ring-1 ring-black
                                    `}>
                                        {type === 'group' ? (
                                            <HiUserGroup className="w-2.5 h-2.5 text-white" />
                                        ) : (
                                            <FaGlobe className="w-2.5 h-2.5 text-white" />
                                        )}
                                    </div>
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                        <h4 className={`font-medium text-sm truncate ${isFromCurrentUser ? 'text-white' : 'text-black'}`}>
                                            {original.name}
                                        </h4>
                                        <FiExternalLink className={`w-3 h-3 flex-shrink-0 ${isFromCurrentUser ? 'text-white/70' : 'text-gray-400'}`} />
                                    </div>
                                    <p className={`text-xs ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                                        {getTypeText()} • Tap to view
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Timestamp */}
                        <div className="flex items-center justify-end gap-1 mt-2">
                            <p className={`text-xs ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                                {formatTime(message.timestamp)}
                            </p>
                            {message.edited && (
                                <span className={`text-xs ${isFromCurrentUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'} italic`}>
                                    (edited)
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForwardedMessage;